import { useState, useEffect, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, AlertCircle } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Drawer } from '../../components/ui/Drawer'
import { Button } from '../../components/ui/Button'
import { VoiceSelector } from '../../components/voice/VoiceSelector'
import type { LeadAdmin } from '../../lib/tipi'
import styles from './LeadForm.module.css'

interface Props {
  aperto: boolean
  /** Se passato, modifica il lead esistente. Altrimenti crea nuovo. */
  lead?: LeadAdmin | null
  onChiudi: () => void
  /** Callback dopo salvataggio riuscito */
  onSalvato: (lead: LeadAdmin) => void
}

/**
 * Drawer per creare o modificare un lead di sistema.
 *
 * Form completo con tutti i campi del lead profile, validazione client-side
 * minima (richiesti + counter), integrazione VoiceSelector per ElevenLabs.
 *
 * Lista di obiezioni e obiettivi gestita come "tag" con + per aggiungere
 * e × per rimuovere — più friendly di un textarea.
 */
export function LeadForm({ aperto, lead, onChiudi, onSalvato }: Props) {
  const queryClient = useQueryClient()
  const editing = !!lead

  // ─── Stato form ─────────────────────────────────────────────────
  const [nome, setNome] = useState('')
  const [ruolo, setRuolo] = useState('')
  const [settore, setSettore] = useState('')
  const [aziendaTipo, setAziendaTipo] = useState('')
  const [personalita, setPersonalita] = useState('')
  const [descrizioneEstesa, setDescrizioneEstesa] = useState('')
  const [difficolta, setDifficolta] = useState<1 | 2 | 3>(2)
  const [isPublic, setIsPublic] = useState(true)
  const [voiceId, setVoiceId] = useState<string | undefined>(undefined)

  const [obiezioni, setObiezioni] = useState<string[]>([])
  const [nuovaObiezione, setNuovaObiezione] = useState('')

  const [obiettivoChiamata, setObiettivoChiamata] = useState('')
  const [obiettiviPossibili, setObiettiviPossibili] = useState<string[]>([])
  const [nuovoObiettivo, setNuovoObiettivo] = useState('')

  const [erroreGlobale, setErroreGlobale] = useState<string | null>(null)

  // Sincronizza con il lead in editing
  useEffect(() => {
    if (!aperto) return
    if (lead) {
      setNome(lead.nome)
      setRuolo(lead.ruolo)
      setSettore(lead.settore)
      setAziendaTipo(lead.aziendaTipo ?? '')
      setPersonalita(lead.personalita)
      setDescrizioneEstesa(lead.descrizioneEstesa ?? '')
      setDifficolta(lead.difficolta)
      setIsPublic(lead.isPublic)
      setVoiceId(lead.voiceId ?? undefined)
      setObiezioni(lead.obiezioni)
      setObiettivoChiamata(lead.obiettivoChiamata ?? '')
      setObiettiviPossibili(lead.obiettiviPossibili)
    } else {
      // Reset per nuovo lead
      setNome(''); setRuolo(''); setSettore(''); setAziendaTipo('')
      setPersonalita(''); setDescrizioneEstesa(''); setDifficolta(2)
      setIsPublic(true); setVoiceId(undefined)
      setObiezioni([]); setObiettivoChiamata(''); setObiettiviPossibili([])
    }
    setNuovaObiezione(''); setNuovoObiettivo(''); setErroreGlobale(null)
  }, [aperto, lead])

  const aggiungiObiezione = () => {
    const v = nuovaObiezione.trim()
    if (v && !obiezioni.includes(v) && obiezioni.length < 10) {
      setObiezioni([...obiezioni, v])
      setNuovaObiezione('')
    }
  }

  const aggiungiObiettivo = () => {
    const v = nuovoObiettivo.trim()
    if (v && !obiettiviPossibili.includes(v) && obiettiviPossibili.length < 10) {
      setObiettiviPossibili([...obiettiviPossibili, v])
      setNuovoObiettivo('')
    }
  }

  const salva = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        nome, ruolo, settore, personalita,
        difficolta, is_public: isPublic, obiezioni,
      }
      if (aziendaTipo.trim()) body.azienda_tipo = aziendaTipo.trim()
      if (descrizioneEstesa.trim()) body.descrizione_estesa = descrizioneEstesa.trim()
      if (obiettivoChiamata.trim()) body.obiettivo_chiamata = obiettivoChiamata.trim()
      if (obiettiviPossibili.length > 0) body.obiettivi_possibili = obiettiviPossibili
      if (voiceId) body.voice_id = voiceId

      if (editing && lead) {
        const res = await api.put<{ dati: LeadAdmin }>(`/superadmin/leads/${lead.id}`, body)
        return res.data.dati!
      } else {
        const res = await api.post<{ dati: LeadAdmin }>('/superadmin/leads', body)
        return res.data.dati!
      }
    },
    onSuccess: (datoSalvato) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] })
      onSalvato(datoSalvato)
    },
    onError: (err) => setErroreGlobale(messaggioErrore(err)),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setErroreGlobale(null)
    if (personalita.trim().length < 10) {
      setErroreGlobale('La personalità deve avere almeno 10 caratteri.')
      return
    }
    salva.mutate()
  }

  return (
    <Drawer
      aperto={aperto}
      titolo={editing ? `Modifica lead — ${lead?.nome}` : 'Nuovo lead di sistema'}
      onChiudi={onChiudi}
      larghezza={620}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variante="ghost" onClick={onChiudi}>Annulla</Button>
          <Button
            type="submit"
            form="lead-form"
            iconaSinistra={<Save size={14} />}
            caricamento={salva.isPending}
          >
            {editing ? 'Salva modifiche' : 'Crea lead'}
          </Button>
        </div>
      }
    >
      <form id="lead-form" onSubmit={submit} className={styles.form}>
        {/* ── Anagrafica ─────────────────────────────────────── */}
        <Sezione titolo="Dati anagrafici">
          <div className={styles.griglia2}>
            <Campo label="Nome *" valore={nome} onChange={setNome} required minLength={2} />
            <Campo label="Ruolo *" valore={ruolo} onChange={setRuolo} required minLength={2} placeholder="es. Direttore acquisti" />
            <Campo label="Settore *" valore={settore} onChange={setSettore} required minLength={2} placeholder="es. Manifattura" />
            <Campo label="Tipo azienda" valore={aziendaTipo} onChange={setAziendaTipo} placeholder="es. PMI metalmeccanica" />
          </div>
        </Sezione>

        {/* ── Profilo comportamentale ───────────────────────── */}
        <Sezione titolo="Profilo comportamentale">
          <CampoTextarea
            label="Personalità *"
            valore={personalita}
            onChange={setPersonalita}
            rows={3}
            placeholder='es. "Scettico sui costi, di fretta, non ama presentazioni lunghe"'
            maxLength={500}
            required
            minLength={10}
          />
          <CampoTextarea
            label="Descrizione estesa (usata dall'AI)"
            valore={descrizioneEstesa}
            onChange={setDescrizioneEstesa}
            rows={4}
            placeholder="Contesto aggiuntivo: ruolo nell'azienda, vissuto, ostacoli decisionali…"
            maxLength={2000}
          />

          <div>
            <label className={styles.label}>Difficoltà</label>
            <div className={styles.difficolta}>
              {[1, 2, 3].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`${styles.diffBtn} ${difficolta === d ? styles.diffSelez : ''}`}
                  onClick={() => setDifficolta(d as 1 | 2 | 3)}
                >
                  {d === 1 ? '○ Facile' : d === 2 ? '◐ Medio' : '● Difficile'}
                </button>
              ))}
            </div>
          </div>
        </Sezione>

        {/* ── Obiezioni ─────────────────────────────────────── */}
        <Sezione titolo="Obiezioni tipiche">
          <TagList
            items={obiezioni}
            onRemove={(i) => setObiezioni(obiezioni.filter((_, idx) => idx !== i))}
            nuovo={nuovaObiezione}
            setNuovo={setNuovaObiezione}
            onAggiungi={aggiungiObiezione}
            placeholder='es. "Troppo caro", "Ci penso e ti faccio sapere"'
            limite={10}
          />
        </Sezione>

        {/* ── Obiettivo chiamata ────────────────────────────── */}
        <Sezione titolo="Obiettivo della chiamata">
          <Campo
            label="Obiettivo default"
            valore={obiettivoChiamata}
            onChange={setObiettivoChiamata}
            placeholder="es. Fissare un appuntamento"
            maxLength={200}
          />

          <div>
            <label className={styles.label}>Obiettivi selezionabili dall'SDR</label>
            <TagList
              items={obiettiviPossibili}
              onRemove={(i) => setObiettiviPossibili(obiettiviPossibili.filter((_, idx) => idx !== i))}
              nuovo={nuovoObiettivo}
              setNuovo={setNuovoObiettivo}
              onAggiungi={aggiungiObiettivo}
              placeholder="Aggiungi un obiettivo selezionabile"
              limite={10}
            />
          </div>
        </Sezione>

        {/* ── Voce ──────────────────────────────────────────── */}
        <Sezione titolo="Voce ElevenLabs">
          <VoiceSelector
            valore={voiceId}
            onChange={setVoiceId}
          />
        </Sezione>

        {/* ── Visibilità ────────────────────────────────────── */}
        <Sezione titolo="Visibilità">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              style={{ accentColor: 'var(--accent)' }}
            />
            <span style={{ fontSize: 14 }}>
              Lead pubblico (visibile a tutti gli utenti)
            </span>
          </label>
        </Sezione>

        {erroreGlobale && (
          <div className={styles.erroreGlobale}>
            <AlertCircle size={14} />
            <span>{erroreGlobale}</span>
          </div>
        )}
      </form>
    </Drawer>
  )
}

// ─── Componenti helper interni ───────────────────────────────────

function Sezione({ titolo, children }: { titolo: string; children: React.ReactNode }) {
  return (
    <div className={styles.sezione}>
      <h3 className={styles.sezioneTitolo}>{titolo}</h3>
      <div className={styles.sezioneCorpo}>{children}</div>
    </div>
  )
}

interface CampoProps {
  label: string
  valore: string
  onChange: (v: string) => void
  placeholder?: string
  required?: boolean
  minLength?: number
  maxLength?: number
}

function Campo({ label, valore, onChange, ...rest }: CampoProps) {
  return (
    <div>
      <label className={styles.label}>{label}</label>
      <input
        type="text"
        value={valore}
        onChange={(e) => onChange(e.target.value)}
        className={styles.input}
        {...rest}
      />
    </div>
  )
}

interface CampoTextareaProps extends CampoProps { rows?: number }

function CampoTextarea({ label, valore, onChange, rows = 3, maxLength, ...rest }: CampoTextareaProps) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <label className={styles.label}>{label}</label>
        {maxLength && (
          <span className={styles.counter}>{valore.length} / {maxLength}</span>
        )}
      </div>
      <textarea
        value={valore}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        maxLength={maxLength}
        className={`${styles.input} ${styles.textarea}`}
        {...rest}
      />
    </div>
  )
}

function TagList({ items, onRemove, nuovo, setNuovo, onAggiungi, placeholder, limite }: {
  items: string[]
  onRemove: (idx: number) => void
  nuovo: string
  setNuovo: (v: string) => void
  onAggiungi: () => void
  placeholder?: string
  limite: number
}) {
  return (
    <div>
      {items.length > 0 && (
        <div className={styles.tags}>
          {items.map((t, i) => (
            <span key={`${t}-${i}`} className={styles.tag}>
              {t}
              <button type="button" onClick={() => onRemove(i)} aria-label="Rimuovi">×</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="text"
          value={nuovo}
          onChange={(e) => setNuovo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onAggiungi() }
          }}
          placeholder={placeholder}
          className={styles.input}
          style={{ flex: 1 }}
          disabled={items.length >= limite}
          maxLength={200}
        />
        <Button
          type="button"
          variante="secondary"
          onClick={onAggiungi}
          disabled={!nuovo.trim() || items.length >= limite}
        >+</Button>
      </div>
      {items.length >= limite && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Limite raggiunto ({limite}).
        </div>
      )}
    </div>
  )
}
